import { ref, computed, unref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { escapeSql } from '@rdrive/helper-sql';
import { makeChartConfig } from '@rdrive/helper-chart-config';

// "Due By Analysis" (Drawings page): issues with a due date as a Gantt — drawing sets → drawings
// → locations → issues, each bar from the first status-history entry to the due date — for
// @rdrive/plugin-gantt's BaseGanttChart. IA_ABSORPTION_PLAN Phase 4 step 13b; was
// components/gantts/IssueDueByGantt.vue, whose query strings these are, VERBATIM apart from the
// escaping: the selected statuses' titles/colours and the parent ids go through escapeSql (the
// SFC hand-escaped the statuses and left the ids raw), and its two identical date-range queries
// (one for the view preset, one for zoom to fit) are the one `dateRange`.
//
// The page renders:
//   <BaseGanttChart :title="$t('dueByAnalysis')" v-bind="chart" :statusOptions="statusOptions"
//     v-model:selectedStatuses="selectedStatuses" :filterContext="issues context"
//     :dataLoaded="ready" @taskSelected="(task) => gridConditions = conditionsFor(task)" />
// `ready` holds the first load back until the statuses are known (the picker starts on every
// non-terminal status, as before). Every query reads `filteredIssues` (the issues context); the
// Gantt registers no filter of its own — a selected row narrows the page's grid through
// `conditionsFor`, WHERE fragments over useIssuesGridData's aliases (`i`, `l`, `ld`).
//
// Rows (BaseGanttChart's shape): { name, level, startDate, endDate, hasChildren, color?,
// statusTitle? } + the ids the next level and `conditionsFor` read — `drawingSetId` (level
// 'drawingSet'), `drawingId` ('drawing', and on its locations), `locationId` ('location'),
// `issueId` ('issue'). Dates go to the base raw (it parses them). A row's `name` is FINAL (the base
// renders it as-is), so the fallback for a row with no title — `noDrawingSet`, `noDrawing`,
// `noLocation` from src/locales/ — is translated here, with the root composer's `t`, when the
// rows are built (the locale is fixed per iframe: the host recreates it to change the locale).

// The top level's row cap (the SFC's `pageSize`)
const PAGE_SIZE = 1000;

export function useIssueDueByGanttChart({ chartName, filterContext, conn, dataLoaded }) {
  const { rows } = makeChartConfig({ chartName, filterContext, conn });
  // The root composer (no local messages): the app's src/locales/ over the vocabulary
  const { t } = useI18n();

  // ---- the status picker
  const statusOptions = ref([]);
  const selectedStatuses = ref([]);
  const statusesReady = ref(false);

  // `AND <column> IN (…)` for the selected statuses, matched by (title, colour); '' for none
  const statusFilter = (column) => (selectedStatuses.value && selectedStatuses.value.length > 0
    ? `AND ${column} IN (
          SELECT id FROM status_templates
          WHERE (title, color) IN (
            ${selectedStatuses.value.map((s) =>
              `('${escapeSql(s.title)}'::text, '${escapeSql(s.color)}'::text)`
            ).join(', ')}
          )
        )`
    : '');

  const getIssueStatuses = async () => rows(`
  SELECT DISTINCT
  s.title AS value,
  s.title AS title,
  s.color as color,
  s.IsTerminal as isTerminal
  FROM status_templates s
  INNER JOIN issues i ON s.id = i.IssueStatusId
  ORDER BY s.sort
  `);

  const initializeStatuses = async () => {
    try {
      statusOptions.value = await getIssueStatuses();
      if (statusOptions.value.length > 0) {
        selectedStatuses.value = statusOptions.value.filter((status) => !status.isTerminal);
      }
    } catch (error) {
      console.error('Error loading issue statuses:', error);
    } finally {
      statusesReady.value = true;
    }
  };

  // ---- the time axis: first status-history entry → latest due date of the selected issues
  const dateRange = async () => {
    const [range] = await rows(`
      SELECT
        MIN(sh.CreatedDate) as overallStartDate,
        MAX(fi.dueBy) as overallEndDate
      FROM filteredIssues fi
      LEFT JOIN status_history sh ON fi.id = sh.SubItemId
      WHERE fi.dueBy IS NOT NULL ${statusFilter('fi.IssueStatusId')}
    `);
    return range ? { start: range.overallStartDate, end: range.overallEndDate } : null;
  };

  // ---- the levels
  // Top: drawing sets
  const loadRootLevel = async () => {
    const drawingSets = await rows(`
      SELECT DISTINCT
        d.DrawingSetId,
        d.DrawingSetTitle,
        d.DrawingSetSort,
        COUNT(DISTINCT fi.id) as issueCount,
        MIN(sh.CreatedDate) as minCreatedDate,
        MIN(fi.dueBy) as minDueDate,
        MAX(fi.dueBy) as maxDueDate
      FROM filteredIssues fi
      LEFT JOIN status_history sh ON fi.id = sh.SubItemId
      LEFT JOIN locations l ON fi.issueLocationId = l.Id
      LEFT JOIN drawings d ON l.DrawingId = d.Id
      WHERE fi.dueBy IS NOT NULL ${statusFilter('fi.IssueStatusId')}
      GROUP BY d.DrawingSetId, d.DrawingSetTitle, d.DrawingSetSort
      ORDER BY d.DrawingSetSort
      LIMIT ${PAGE_SIZE}
    `);

    return drawingSets.map((drawingSet) => ({
      name: drawingSet.DrawingSetTitle || t('noDrawingSet'),
      level: 'drawingSet',
      drawingSetId: drawingSet.DrawingSetId,
      startDate: drawingSet.minCreatedDate || drawingSet.minDueDate,
      endDate: drawingSet.maxDueDate,
      hasChildren: Number(drawingSet.issueCount) > 0
    }));
  };

  // A drawing set's drawings
  const loadDrawingsForSet = async ({ drawingSetId }) => {
    const drawings = await rows(`
      SELECT DISTINCT
        d.Id as DrawingId,
        d.Title as DrawingTitle,
        d.Sort as drawingSort,
        COUNT(DISTINCT fi.id) as issueCount,
        MIN(sh.CreatedDate) as minCreatedDate,
        MIN(fi.dueBy) as minDueDate,
        MAX(fi.dueBy) as maxDueDate
      FROM filteredIssues fi
      LEFT JOIN status_history sh ON fi.id = sh.SubItemId
      LEFT JOIN locations l ON fi.issueLocationId = l.Id
      LEFT JOIN drawings d ON l.DrawingId = d.Id
      WHERE fi.dueBy IS NOT NULL AND d.DrawingSetId = '${escapeSql(drawingSetId)}' ${statusFilter('fi.IssueStatusId')}
      GROUP BY d.Id, d.Title, d.Sort
      ORDER BY d.Sort
    `);

    return drawings.map((drawing) => ({
      name: drawing.DrawingTitle || t('noDrawing'),
      level: 'drawing',
      drawingId: drawing.DrawingId,
      startDate: drawing.minCreatedDate || drawing.minDueDate,
      endDate: drawing.maxDueDate,
      hasChildren: Number(drawing.issueCount) > 0
    }));
  };

  // A drawing's locations (the query selects no minDueDate, so a location without history has
  // no start — as before)
  const loadLocationsForDrawing = async ({ drawingId }) => {
    const locations = await rows(`
      SELECT DISTINCT
        l.Id as locationId,
        l.Title as locationTitle,
        COUNT(DISTINCT fi.id) as issueCount,
        MIN(sh.CreatedDate) as minCreatedDate,
        MAX(fi.dueBy) as maxDueDate
      FROM filteredIssues fi
      LEFT JOIN status_history sh ON fi.id = sh.SubItemId
      LEFT JOIN locations l ON fi.issueLocationId = l.Id
      WHERE fi.dueBy IS NOT NULL AND l.DrawingId = '${escapeSql(drawingId)}' ${statusFilter('fi.IssueStatusId')}
      GROUP BY l.Id, l.Title
      ORDER BY l.Title
    `);

    return locations.map((location) => ({
      name: location.locationTitle || t('noLocation'),
      level: 'location',
      locationId: location.locationId,
      drawingId,
      startDate: location.minCreatedDate || location.minDueDate,
      endDate: location.maxDueDate,
      hasChildren: Number(location.issueCount) > 0
    }));
  };

  // A location's issues — the leaves, coloured by their status
  const loadIssuesForLocation = async ({ locationId }) => {
    const issues = await rows(`
      SELECT
        fi.id,
        fi.issueCategory,
        fi.issueItem,
        MIN(sh.CreatedDate) as createdDate,
        fi.dueBy as dueDate,
        fi.issueStatusId,
        st.title as statusTitle,
        st.color as statusColor
      FROM filteredIssues fi
      LEFT JOIN status_history sh ON fi.id = sh.SubItemId
      LEFT JOIN status_templates st ON fi.issueStatusId = st.id
      WHERE fi.dueBy IS NOT NULL AND fi.issueLocationId = '${escapeSql(locationId)}' ${statusFilter('fi.IssueStatusId')}
      GROUP BY fi.id, fi.issueCategory, fi.issueItem, fi.dueBy, fi.issueStatusId, st.title, st.color
      ORDER BY MIN(sh.CreatedDate)
    `);

    return issues.map((issue) => ({
      name: issue.issueCategory || issue.issueItem || t('issue'),
      level: 'issue',
      issueId: issue.id,
      startDate: issue.createdDate,
      endDate: issue.dueDate,
      color: issue.statusColor,
      statusTitle: issue.statusTitle,
      hasChildren: false
    }));
  };

  // A selected row → the grid's WHERE fragments (DrawingsPage's handler until 13b; the values
  // are escaped now). No row, or one without its id → no condition.
  const conditionsFor = (task) => {
    if (!task) return [];
    if (task.level === 'issue' && task.issueId) {
      return [`i.id = '${escapeSql(task.issueId)}'`];
    }
    if (task.level === 'location' && task.locationId) {
      return [`i.issueLocationId = '${escapeSql(task.locationId)}'`];
    }
    if (task.level === 'drawing' && task.drawingId) {
      return [`l.drawingId = '${escapeSql(task.drawingId)}'`];
    }
    if (task.level === 'drawingSet' && task.drawingSetId) {
      return [`ld.DrawingSetId = '${escapeSql(task.drawingSetId)}'`];
    }
    return [];
  };

  // Once the page's data is loaded: the statuses (+ the initial selection)
  watch(() => unref(dataLoaded), (loaded) => {
    if (!loaded) return;
    if (statusOptions.value.length === 0) initializeStatuses();
  }, { immediate: true });

  return {
    // spread onto BaseGanttChart
    chart: {
      // a row's children come from the loader named by its `level`
      levels: {
        root: loadRootLevel,
        drawingSet: loadDrawingsForSet,
        drawing: loadLocationsForDrawing,
        location: loadIssuesForLocation
      },
      dateRange
    },
    ready: computed(() => !!unref(dataLoaded) && statusesReady.value),
    // the status picker
    statusOptions,
    selectedStatuses,
    // the page's grid
    conditionsFor
  };
}
